import type { EventInfo } from '../data/types'

/** Which sub-sections of the event info to render. */
export interface EventInfoSections {
  welcome?: boolean
  venue?: boolean
  travel?: boolean
  dressCode?: boolean
  faqs?: boolean
  contacts?: boolean
}

/** Default: all sub-sections (used by the standalone Event Info page). */
export const ALL_EVENT_INFO_SECTIONS: Required<EventInfoSections> = {
  welcome: true,
  venue: true,
  travel: true,
  dressCode: true,
  faqs: true,
  contacts: true,
}

/**
 * Per-section defaults for the "Event Info (dynamic)" page-builder block:
 * welcome + venue on, the rest opt-in. Kept in sync with the block editor's
 * checkbox defaults so a saved config that omits a key falls back to its default
 * (rather than being treated as off).
 */
export const EVENT_INFO_BLOCK_DEFAULTS: Required<EventInfoSections> = {
  welcome: true,
  venue: true,
  travel: false,
  dressCode: false,
  faqs: false,
  contacts: false,
}

/**
 * Resolve the effective sections for the block: start from the per-section
 * defaults and overlay any explicitly-saved values. This ensures a default-on
 * section (welcome/venue) the admin never toggled still renders, while explicit
 * choices (including turning a default-on section off) are honored.
 */
export function resolveBlockSections(
  configured: Record<string, boolean> | null | undefined,
): EventInfoSections {
  return { ...EVENT_INFO_BLOCK_DEFAULTS, ...(configured ?? {}) }
}

/** True if the event has any content for the given sub-sections (for gating). */
export function hasEventInfoContent(
  event: EventInfo | null | undefined,
  sections: EventInfoSections = ALL_EVENT_INFO_SECTIONS,
): boolean {
  if (!event) return false
  const venue = event.venue
  return Boolean(
    (sections.welcome && event.welcomeMessage) ||
      (sections.venue && venue && (venue.name || venue.address || venue.city)) ||
      (sections.travel && event.travel) ||
      (sections.dressCode && event.dressCode) ||
      (sections.faqs && event.faqs?.length) ||
      (sections.contacts && event.contacts?.length),
  )
}
